import type { ComponentInternalInstance, ComponentPublicInstance } from 'vue';

import { VGL_ERROR_CODE } from '@/services/errorHandler/errorCodes';
import { VglErrorMessages } from '@/services/errorHandler/errorMessages';
import type {
  ErrorToHandle,
  VGL_ERROR_HANDLER_SERVICE,
  VGLErrorHandlerService,
} from '@/services/errorHandler/types';
import { VglError } from '@/services/errorHandler/vglError';
import { useErrorModal } from '@/utils/useErrorModal/useErrorModal';

export class ErrorHandler {
  private readonly services: Map<symbol, VGLErrorHandlerService>;

  private readonly stackTraceReader: (
    error: VglError,
    instance: ComponentPublicInstance | ComponentInternalInstance | null,
    info: string,
  ) => Promise<ErrorToHandle>;

  constructor(
    services: Map<symbol, VGLErrorHandlerService>,
    stackTraceReader: (
      error: VglError,
      instance: ComponentPublicInstance | ComponentInternalInstance | null,
      info: string,
    ) => Promise<ErrorToHandle>,
  ) {
    this.services = services;
    this.stackTraceReader = stackTraceReader;

    if (this.services.size === 0) {
      console.error(
        VglErrorMessages[VGL_ERROR_CODE.GENERAL_ERROR_HANDLER_CONSTRUCTOR],
      );
    }
  }

  handle(
    e: unknown,
    instance: ComponentPublicInstance | ComponentInternalInstance | null = null,
    info: string = '',
  ): void {
    let error;
    if (!(e instanceof VglError)) {
      error = new VglError(VGL_ERROR_CODE.GENERAL_GENERIC, {
        additionalErrorMessage: e as string,
      });
    } else {
      error = e;
    }

    this.createError(error, instance, info);
  }

  private createError(
    error: VglError,
    instance: ComponentPublicInstance | ComponentInternalInstance | null,
    info: string,
  ): void {
    const optionalModalMessage = error.getUserModalMessage();
    const optionalModalTitle = error.getUserModalTitle();
    const optionalErrorCallback = error.getErrorCallback();

    if (optionalErrorCallback !== undefined) {
      optionalErrorCallback();
    }

    if (optionalModalMessage !== undefined) {
      ErrorHandler.handleUserMessage(
        error.getErrorCode(),
        optionalModalMessage,
        optionalModalTitle,
        error.getIsNonClosableUserModal(),
      );
    }

    this.stackTraceReader(error, instance, info)
      .then((vglError) => {
        this.handleError(vglError);
      })
      .catch(() => {
        this.handleError({
          error,
          info,
          instance,
          timestamp: new Date(),
          stackTrace: [],
        });
      });
  }

  private handleError(error: ErrorToHandle): void {
    const servicesToSkip = error.error.getServiceHandlerToSkip();
    this.services.forEach((service, index) => {
      if (
        servicesToSkip.includes(index.description as VGL_ERROR_HANDLER_SERVICE)
      ) {
        return;
      }

      service.handle(error);
    });
  }

  private static handleUserMessage(
    errorCode: VGL_ERROR_CODE,
    modalMessage: string,
    modalTitle?: string,
    isNonClosableUserModal?: boolean,
  ): void {
    const { updateData, open } = useErrorModal();

    updateData(errorCode, modalTitle, modalMessage, isNonClosableUserModal);
    open();
  }
}
